import React from 'react';

import { Box, Typography } from '@mui/material';
import BackdropDialog from 'common/components/BackdropDialog';

import { DeliveryOptionWithProducts } from 'common/types';
import { useTranslation } from 'services/localization/useTranslation';
import { removeDeliveryOption } from 'common/api/frontend';
import { replace, Routes } from 'routing';
import errorHandler from 'common/services/errorHandler';

interface Props {
	value: DeliveryOptionWithProducts;
	open: boolean;
	onClose: () => void;
	setLoading?: (loading: boolean) => void;
}

const DeleteDeliveryDialog = (props: Props) => {
	const { value, open, onClose, setLoading } = props;
	const { t } = useTranslation();

	const handleDelete = async () => {
		setLoading?.(true);
		try {
			await removeDeliveryOption(value.id, value.products);
			replace(Routes.DELIVERY);
		} catch (e) {
			errorHandler.report(e);
		} finally {
			setLoading?.(false);
		}
	};

	return (
		<BackdropDialog
			isOpen={open}
			onClose={onClose}
			label={t('delivery.deleteRow', {
				defaultValue: 'Delete {{name}}',
				name: value.name.def,
			})}
			body={
				<>
					<Typography variant="body2">
						{t('delivery.deleteWarningTop2', {
							defaultValue:
								"Deleting the delivery option will remove {{name}} from online. Already received orders won't be changed.",
							name: value.name.def,
						})}
					</Typography>
					<Box mt={3} />
					<Typography variant="body2">
						{t('delivery.deleteWarningBottom', 'This action cannot be undone.')}
					</Typography>
				</>
			}
			variant="danger"
			width="xs"
			confirm={{
				label: t('common:actions.delete'),
				callback: () => handleDelete(),
				closeAfterDone: true,
			}}
			cancel={{
				label: t('common:actions.cancel'),
			}}
		/>
	);
};

export default DeleteDeliveryDialog;
